import React, { useEffect, useRef, useState } from 'react';
import { Mic, Minimize2, Maximize2 } from 'lucide-react';

// =====================================================================
// PLENARIUM:CRONOMETRO_TABLET
// Cronometro do Uso da Palavra no tablet do vereador. Recebe o mesmo
// aviso do socket que a TV ('cronometro-update', ja tratado no
// PainelVereador) e mostra o orador, a foto e o tempo em tela cheia.
// Pode ser minimizado para uma pilula flutuante. Durante uma votacao
// aberta ele fica minimizado sozinho, para nunca atrapalhar o voto.
// O fundo e escuro de proposito: fica igual nos temas claro e escuro.
// =====================================================================

const CSS_CRONO = `
.pct-ov { position: fixed; inset: 0; z-index: 9500; display: flex; flex-direction: column; color: #fff;
  font-family: inherit; overflow: hidden; animation: pct-entra .35s ease-out;
  background: radial-gradient(120% 80% at 50% 115%, color-mix(in srgb, var(--cor) 26%, transparent) 0%, transparent 60%),
              radial-gradient(90% 70% at 0% 0%, rgba(37,99,235,.28) 0%, transparent 60%),
              linear-gradient(160deg, #061230 0%, #0a1f4a 55%, #071634 100%); }
@supports not (background: color-mix(in srgb, red 10%, transparent)) {
  .pct-ov { background: radial-gradient(90% 70% at 0% 0%, rgba(37,99,235,.28) 0%, transparent 60%), linear-gradient(160deg, #061230 0%, #0a1f4a 55%, #071634 100%); }
}
@keyframes pct-entra { from { opacity: 0; transform: scale(1.02); } to { opacity: 1; transform: none; } }
.pct-topo { display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: calc(14px + env(safe-area-inset-top)) 20px 0; }
.pct-chip { display: inline-flex; align-items: center; gap: 9px; padding: 8px 14px; border-radius: 999px;
  background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.14); font-size: .78rem; font-weight: 800; letter-spacing: 2.4px; white-space: nowrap; }
.pct-ponto { width: 9px; height: 9px; border-radius: 50%; background: var(--cor); box-shadow: 0 0 0 0 var(--cor); animation: pct-pulsa 1.6s infinite; }
.pct-ov.pct-pausado .pct-ponto, .pct-mini.pct-pausado .pct-ponto { animation: none; }
@keyframes pct-pulsa { 0% { box-shadow: 0 0 0 0 rgba(255,255,255,.55); } 70% { box-shadow: 0 0 0 10px rgba(255,255,255,0); } 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); } }
.pct-min { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 12px; cursor: pointer;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.18); color: #fff; font: inherit; font-size: .88rem; font-weight: 700; }
.pct-min:active { transform: scale(.97); }
.pct-corpo { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; gap: clamp(24px, 6vw, 80px); padding: 16px 24px 28px; }
.pct-orador { display: flex; flex-direction: column; align-items: center; min-width: 0; max-width: 44vw; }
.pct-foto { height: min(52vh, 380px); aspect-ratio: 3 / 4; border-radius: 26px; object-fit: cover; display: block;
  border: 4px solid var(--cor); box-shadow: 0 0 0 8px color-mix(in srgb, var(--cor) 16%, transparent), 0 24px 60px rgba(0,0,0,.45);
  background: rgba(255,255,255,.06); transition: border-color .4s; }
.pct-ini { display: flex; align-items: center; justify-content: center; font-size: min(14vh, 110px); font-weight: 900; color: rgba(255,255,255,.35); }
.pct-voce { margin-top: 16px; padding: 6px 14px; border-radius: 999px; background: var(--cor); color: #04122e; font-size: .8rem; font-weight: 900; letter-spacing: 1.4px; text-transform: uppercase; }
.pct-trat { margin-top: 14px; font-size: .82rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: rgba(255,255,255,.6); }
.pct-voce + .pct-trat { margin-top: 10px; }
.pct-nome { margin-top: 4px; font-size: clamp(1.4rem, 3.4vw, 2.3rem); font-weight: 900; line-height: 1.12; text-align: center; overflow-wrap: anywhere; }
.pct-partido { margin-top: 6px; font-size: .95rem; font-weight: 600; color: rgba(255,255,255,.55); }
.pct-relogio { position: relative; width: min(64vh, 44vw, 460px); aspect-ratio: 1; flex: 0 0 auto; }
.pct-relogio svg { overflow: visible; position: absolute; inset: 0; width: 100%; height: 100%; transform: rotate(-90deg); }
.pct-trilha { fill: none; stroke: rgba(255,255,255,.09); }
.pct-arco { fill: none; stroke: var(--cor); stroke-linecap: round; transition: stroke-dashoffset 1s linear, stroke .4s;
  filter: drop-shadow(0 0 10px color-mix(in srgb, var(--cor) 70%, transparent)); }
.pct-centro { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.pct-tempo { font-size: min(15vh, 10.5vw, 112px); font-weight: 800; line-height: 1; letter-spacing: -1px; font-variant-numeric: tabular-nums; color: #fff;
  text-shadow: 0 0 40px color-mix(in srgb, var(--cor) 55%, transparent); }
.pct-rot { margin-top: 12px; font-size: .78rem; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: var(--cor); }
.pct-final .pct-tempo { animation: pct-bate 1s ease-in-out infinite; }
@keyframes pct-bate { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.06); } }
.pct-pausado .pct-tempo { opacity: .55; }
.pct-faixa { display: inline-flex; align-items: center; gap: 8px; margin-top: 14px; padding: 7px 14px; border-radius: 999px; background: rgba(251,191,36,.16); color: #fde68a; font-size: .8rem; font-weight: 800; letter-spacing: 1.6px; }

/* Retrato (tablet em pe e celular): um embaixo do outro */
@media (orientation: portrait), (max-width: 700px) {
  .pct-corpo { flex-direction: column; gap: 22px; padding: 8px 20px 28px; }
  .pct-orador { max-width: 92vw; }
  .pct-foto { height: min(28vh, 260px); border-radius: 22px; }
  .pct-relogio { width: min(46vh, 78vw, 400px); }
  .pct-tempo { font-size: min(10vh, 17vw, 100px); }
}
@media (max-height: 520px) and (orientation: landscape) {
  .pct-foto { height: 46vh; }
  .pct-relogio { width: min(62vh, 44vw); }
  .pct-trat { margin-top: 8px; }
}

/* ---- Tablet e celular (modelo aprovado): fundo preto, foto ao lado do nome, anel grande ---- */
.pct-ov.pct-m { background: #0b0b0c; animation: pct-entra .3s ease-out; }
.pct-m .pct-topo { padding: calc(16px + env(safe-area-inset-top)) 20px 0; }
.pct-m .pct-chip { background: #1b1b1e; border-color: #2a2a2e; letter-spacing: 1.6px; font-size: .8rem; }
.pct-m .pct-ponto { width: 10px; height: 10px; box-shadow: 0 0 0 3px color-mix(in srgb, var(--cor) 35%, transparent); animation: pct-pulsa-m 1.6s infinite; }
@keyframes pct-pulsa-m { 0%, 100% { opacity: 1; } 50% { opacity: .55; } }
.pct-m .pct-min { background: #1b1b1e; border-color: #2a2a2e; border-radius: 12px; }
.pct-m-corpo { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 18px 22px 0; }
.pct-m-centro { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; container-type: size; padding: 12px 0; }
.pct-m-espaco { flex: 0 0 auto; padding-bottom: calc(54px + env(safe-area-inset-top)); visibility: hidden; }
.pct-m-espaco .pct-m-foto { border-color: transparent; }
.pct-m-info { display: flex; align-items: center; gap: 16px; width: 100%; flex: 0 0 auto; }
.pct-m-foto { width: clamp(84px, 24vw, 150px); aspect-ratio: 1; flex: 0 0 auto; border-radius: 14px; object-fit: cover; object-position: top;
  border: 2.5px solid var(--cor); background: #1b1b1e; display: flex; align-items: center; justify-content: center; font-size: 2rem; font-weight: 900; color: rgba(255,255,255,.35); transition: border-color .4s; }
.pct-m-div { align-self: stretch; width: 1px; background: #2c2c30; margin: 4px 0; flex: 0 0 auto; }
.pct-m-txt { display: flex; flex-direction: column; min-width: 0; gap: 3px; }
.pct-m-trat { font-size: .74rem; font-weight: 700; letter-spacing: 2.6px; text-transform: uppercase; color: #8e8e95; }
.pct-m-nome { font-size: clamp(1.15rem, 4.4vw, 2rem); font-weight: 900; line-height: 1.12; text-transform: uppercase; color: #fff; overflow-wrap: anywhere; }
.pct-m-partido { font-size: .9rem; font-weight: 600; color: #8e8e95; }
.pct-m-voce { margin-top: 6px; align-self: flex-start; white-space: nowrap; padding: 4px 10px; border-radius: 999px; background: var(--cor); color: #0b0b0c; font-size: .62rem; font-weight: 900; letter-spacing: .6px; text-transform: uppercase; }
.pct-m-relogio { position: relative; width: min(84vw, 46vh, 560px); aspect-ratio: 1; flex: 0 0 auto; }
@supports (width: 1cqh) { .pct-m-relogio { width: min(100cqw, 100cqh, 560px); container-type: inline-size; } .pct-m .pct-m-relogio .pct-tempo { font-size: 25cqw; } .pct-m .pct-m-relogio .pct-rot { font-size: max(.8rem, 3.6cqw); } }
.pct-m-relogio svg { overflow: visible; position: absolute; inset: 0; width: 100%; height: 100%; transform: rotate(-90deg); }
.pct-m .pct-trilha { stroke: #26262a; }
.pct-m .pct-arco { filter: none; }
.pct-m .pct-tempo { font-size: min(19vw, 12vh, 132px); font-weight: 800; letter-spacing: -1px; color: #fbf1dc; text-shadow: none; }
.pct-m .pct-rot { margin-top: 8px; font-size: .9rem; font-weight: 800; letter-spacing: 2.4px; }
@media (orientation: landscape) { .pct-m-corpo { padding-top: 10px; } .pct-m-foto { width: clamp(64px, 15vh, 130px); } }

/* Pilula minimizada */
.pct-mini { position: fixed; right: 16px; bottom: calc(18px + env(safe-area-inset-bottom)); z-index: 9400;
  display: flex; align-items: center; gap: 12px; padding: 8px 16px 8px 8px; border-radius: 999px; cursor: pointer; color: #fff;
  background: linear-gradient(135deg, rgba(8,24,60,.96), rgba(15,40,94,.96)); border: 1px solid rgba(255,255,255,.16);
  box-shadow: 0 14px 34px rgba(2,8,23,.45), 0 0 0 3px color-mix(in srgb, var(--cor) 35%, transparent);
  font: inherit; text-align: left; max-width: calc(100vw - 32px); animation: pct-sobe .3s ease-out; -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
@keyframes pct-sobe { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.pct-mini-anel { position: relative; width: 50px; height: 50px; flex: 0 0 auto; }
.pct-mini-anel svg { overflow: visible; position: absolute; inset: 0; transform: rotate(-90deg); }
.pct-mini-anel img, .pct-mini-anel .pct-mini-ini { position: absolute; inset: 6px; width: 38px; height: 38px; border-radius: 50%; object-fit: cover; object-position: top; background: rgba(255,255,255,.1); }
.pct-mini-ini { display: flex; align-items: center; justify-content: center; font-size: .85rem; font-weight: 900; }
.pct-mini-txt { display: flex; flex-direction: column; min-width: 0; }
.pct-mini-rot { display: flex; align-items: center; gap: 6px; font-size: .62rem; font-weight: 800; letter-spacing: 1.8px; color: rgba(255,255,255,.6); text-transform: uppercase; }
.pct-mini-rot .pct-ponto { width: 7px; height: 7px; }
.pct-mini-nome { font-size: .9rem; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 44vw; }
.pct-mini-tempo { font-size: 1.55rem; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--cor); margin-left: 4px; }
.pct-mini svg.pct-abrir { opacity: .6; flex: 0 0 auto; }
@media (max-width: 420px) { .pct-chip { letter-spacing: 1.4px; padding: 8px 12px; } .pct-min { padding: 9px 12px; } }
@media (max-width: 768px) { .pct-mini { bottom: calc(86px + env(safe-area-inset-bottom)); right: 12px; } }

/* Tempo encerrado */
.pct-fim { position: fixed; inset: 0; z-index: 9600; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #fff; text-align: center; padding: 24px;
  background: radial-gradient(80% 60% at 50% 50%, #b91c1c 0%, #7f1d1d 100%); animation: pct-entra .25s ease-out; }
.pct-fim b { font-size: clamp(2rem, 7vw, 4.4rem); font-weight: 900; letter-spacing: 2px; text-transform: uppercase; line-height: 1.05; }
.pct-fim span { font-size: clamp(1rem, 2.6vw, 1.5rem); font-weight: 700; opacity: .85; }
.pct-fim-icone { width: 74px; height: 74px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,.14); margin-bottom: 8px; }
`;

const fmt = (seg) => {
  const s = Math.max(0, Math.floor(seg || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const corDo = (c, verde) => {
  if (!c) return verde;
  if (c.pausado) return '#94a3b8';
  return c.tempo > 60 ? verde : c.tempo > 30 ? '#f59e0b' : '#ef4444';
};
// Computador (mouse e tela grande) mantem o layout de duas colunas.
const MQ_PC = '(min-width: 1025px) and (hover: hover) and (pointer: fine)';
function useEhPC() {
  const [pc, setPc] = useState(() => !!(window.matchMedia && window.matchMedia(MQ_PC).matches));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(MQ_PC); const f = () => setPc(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', f); else if (mq.addListener) mq.addListener(f);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', f); else if (mq.removeListener) mq.removeListener(f); };
  }, []);
  return pc;
}
const tratamentoDe = (v) => {
  if (!v) return 'Orador';
  if (v.cargo === 'PRESIDENTE') return 'Presidente';
  if (v.cargo === 'VICE_PRESIDENTE') return 'Vice-Presidente';
  return v.sexo === 'F' ? 'Vereadora' : 'Vereador';
};
const iniciais = (n) => String(n || '').trim().split(/\s+/).filter(Boolean).map(x => x[0]).slice(0, 2).join('').toUpperCase();

function Anel({ frac, tam, esp }) {
  const r = (tam - esp) / 2; const c = 2 * Math.PI * r;
  return (
    <svg viewBox={`0 0 ${tam} ${tam}`} aria-hidden="true">
      <circle className="pct-trilha" cx={tam / 2} cy={tam / 2} r={r} strokeWidth={esp} />
      <circle className="pct-arco" cx={tam / 2} cy={tam / 2} r={r} strokeWidth={esp} strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(0, Math.min(1, frac)))} />
    </svg>
  );
}

export default function CronometroTablet({ cron, vereadores = [], usuario = null, votacaoAberta = false }) {
  const [minimizado, setMinimizado] = useState(false);
  const [fim, setFim] = useState(null);
  const totais = useRef({});
  const ultimo = useRef(null);
  const [dispensado, setDispensado] = useState(false);
  const ehPC = useEhPC();
  const nomeAtual = cron ? (cron.nome || '') : null;
  const zerado = !!(cron && !cron.pausado && cron.tempo <= 0);

  // Igual a TV: o tablet conta sozinho e cada aviso do servidor corrige.
  // Protecao a mais: se zerou e o aviso de encerramento nao chegou em
  // 6 segundos (internet oscilou), fecha sozinho com "Tempo encerrado".
  useEffect(() => {
    if (!zerado) { setDispensado(false); return undefined; }
    const t = setTimeout(() => setDispensado(true), 6000);
    return () => clearTimeout(t);
  }, [zerado]);

  // Orador novo: abre em tela cheia de novo (a nao ser em votacao).
  useEffect(() => { if (nomeAtual !== null) setMinimizado(false); }, [nomeAtual]);

  // Guarda o maior tempo visto deste orador: e a base do anel.
  if (cron) {
    const k = cron.nome || '-';
    if (!totais.current[k] || cron.tempo > totais.current[k]) totais.current[k] = cron.tempo;
  }

  // Quando o cronometro sai: se acabou o tempo, mostra "Tempo encerrado".
  const ativo = cron && !dispensado ? cron : null;
  useEffect(() => {
    if (ativo) { ultimo.current = ativo; return undefined; }
    const u = ultimo.current; ultimo.current = null;
    if (!u || u.pausado || u.tempo > 2) return undefined;
    setFim(u);
    const t = setTimeout(() => setFim(null), 3500);
    return () => clearTimeout(t);
  }, [ativo]);

  const vFim = fim ? vereadores.find(x => x.nome === fim.nome) : null;
  const telaFim = fim && !votacaoAberta ? (
    <div className="pct-fim" onClick={() => setFim(null)}>
      <div className="pct-fim-icone"><Mic size={36} /></div>
      <b>Tempo encerrado</b>
      <span>{vFim ? `${tratamentoDe(vFim)} ${vFim.nomeParlamentar || vFim.nome}` : (fim.nome || 'Orador')}</span>
    </div>
  ) : null;

  if (!cron || dispensado) return telaFim ? <><style>{CSS_CRONO}</style>{telaFim}</> : null;

  const v = vereadores.find(x => x.nome === cron.nome) || null;
  const nome = (v && (v.nomeParlamentar || v.nome)) || cron.nome || 'Orador';
  const trat = v ? tratamentoDe(v) : 'Orador convidado';
  const souEu = !!(usuario && ((usuario.id && v && v.id === usuario.id) || (usuario.nome && usuario.nome === cron.nome)));
  const total = totais.current[cron.nome || '-'] || cron.tempo || 1;
  const frac = total > 0 ? cron.tempo / total : 0;
  const cor = corDo(cron, ehPC ? '#22c55e' : '#8ee21f');
  const acabou = !cron.pausado && cron.tempo <= 0;
  const final = !cron.pausado && cron.tempo > 0 && cron.tempo <= 10;
  const cls = `${cron.pausado ? ' pct-pausado' : ''}${final ? ' pct-final' : ''}`;
  const foto = cron.foto || (v && v.fotoUrl) || '';
  const rotulo = acabou ? 'Tempo encerrado' : cron.pausado ? 'Pausado' : 'restantes';

  if (minimizado || votacaoAberta) return (
    <>
      <style>{CSS_CRONO}</style>
      <button type="button" className={`pct-mini${cls}`} style={{ '--cor': cor }} onClick={() => setMinimizado(false)} aria-label="Abrir cronometro do uso da palavra">
        <span className="pct-mini-anel">
          <Anel frac={frac} tam={50} esp={4} />
          {foto ? <img src={foto} alt="" /> : <span className="pct-mini-ini">{iniciais(nome) || <Mic size={16} />}</span>}
        </span>
        <span className="pct-mini-txt">
          <span className="pct-mini-rot"><span className="pct-ponto" />{souEu ? 'Sua fala' : 'Uso da palavra'}</span>
          <span className="pct-mini-nome">{nome}</span>
        </span>
        <span className="pct-mini-tempo">{fmt(cron.tempo)}</span>
        <Maximize2 className="pct-abrir" size={16} />
      </button>
    </>
  );

  if (!ehPC) return (
    <>
      <style>{CSS_CRONO}</style>
      <div className={`pct-ov pct-m${cls}`} style={{ '--cor': cor }} role="dialog" aria-label="Cronometro do uso da palavra">
        <div className="pct-topo">
          <span className="pct-chip"><span className="pct-ponto" />USO DA PALAVRA</span>
          <button type="button" className="pct-min" onClick={() => setMinimizado(true)}><Minimize2 size={16} />Minimizar</button>
        </div>
        <div className="pct-m-corpo">
          <div className="pct-m-info">
            {foto
              ? <img className="pct-m-foto" src={foto} alt="" />
              : <div className="pct-m-foto">{iniciais(nome) || <Mic size={32} />}</div>}
            <span className="pct-m-div" />
            <div className="pct-m-txt">
              <span className="pct-m-trat">{trat}</span>
              <span className="pct-m-nome">{nome}</span>
              {v && v.partido && <span className="pct-m-partido">{v.partido}</span>}
              {souEu && <span className="pct-m-voce">Você está com a palavra</span>}
            </div>
          </div>
          <div className="pct-m-centro">
            <div className="pct-m-relogio">
              <Anel frac={frac} tam={200} esp={13} />
              <div className="pct-centro">
                <span className="pct-tempo">{fmt(cron.tempo)}</span>
                <span className="pct-rot">{rotulo}</span>
              </div>
            </div>
          </div>
          {/* espaco igual ao de cima: deixa o relogio exatamente no meio da tela */}
          <div className="pct-m-espaco" aria-hidden="true"><div className="pct-m-foto" /></div>
        </div>
      </div>
      {telaFim}
    </>
  );

  return (
    <>
      <style>{CSS_CRONO}</style>
      <div className={`pct-ov${cls}`} style={{ '--cor': cor }} role="dialog" aria-label="Cronometro do uso da palavra">
        <div className="pct-topo">
          <span className="pct-chip"><span className="pct-ponto" />USO DA PALAVRA</span>
          <button type="button" className="pct-min" onClick={() => setMinimizado(true)}><Minimize2 size={16} />Minimizar</button>
        </div>
        <div className="pct-corpo">
          <div className="pct-orador">
            {foto
              ? <img className="pct-foto" src={foto} alt="" />
              : <div className="pct-foto pct-ini">{iniciais(nome) || <Mic size={64} />}</div>}
            {souEu && <span className="pct-voce">Você está com a palavra</span>}
            <span className="pct-trat">{trat}</span>
            <span className="pct-nome">{nome}</span>
            {v && v.partido && <span className="pct-partido">{v.partido}</span>}
          </div>
          <div className="pct-relogio">
            <Anel frac={frac} tam={200} esp={9} />
            <div className="pct-centro">
              <span className="pct-tempo">{fmt(cron.tempo)}</span>
              <span className="pct-rot">{rotulo}</span>
            </div>
          </div>
        </div>
      </div>
      {telaFim}
    </>
  );
}
